import React, { useEffect, useState } from "react";
import { adminCosts, adminSaveCostSettings } from "@/lib/api";
import { Loader2, AlertTriangle, Save } from "lucide-react";
import { toast } from "sonner";

/* Where the money goes, and what comes in.
 *
 * Every figure here is measured or explicitly absent — the same standard a
 * client briefing is held to, applied to the operator's own P&L. A cost nobody
 * has entered renders as "not entered" with the reason, never as zero and never
 * as an estimate: a dashboard that quietly guesses is worse than one that
 * admits a gap, because the operator would price the business on the guess.
 *
 * Contrast rule (CLAUDE.md §1): logo orange is never text on a light surface.
 * Figures that need the orange family use dark rust #9A3412 (6.54:1).
 */

const money = (v, cur = "A$") =>
  v === null || v === undefined ? null : `${cur}${Number(v).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const usd = (v) => (v === null || v === undefined ? null : `US$${Number(v).toFixed(4)}`);

function Unavailable({ reason }) {
  return (
    <span className="font-mono text-[11px] text-[#525252] italic" title={reason}>
      not entered
    </span>
  );
}

function Tile({ label, value, sub, unavailable, tone = "ink" }) {
  const toneClass = tone === "rust" ? "text-[#9a3412]" : "text-[#171717]";
  return (
    <div className="border border-[#171717]/12 bg-[#F5F2ED] p-5">
      <div className="tick text-[#525252] mb-2">{label}</div>
      <div className={`font-display text-[28px] leading-none ${toneClass}`}>
        {value ?? <span className="text-[15px] text-[#525252] italic">unavailable</span>}
      </div>
      {sub && <div className="mt-2 text-[11px] font-mono text-[#525252] leading-snug">{sub}</div>}
      {unavailable && (
        <div className="mt-2 text-[11px] font-mono text-[#9a3412] leading-snug">{unavailable}</div>
      )}
    </div>
  );
}

/* Entering what you were invoiced.
 *
 * These are typed in because nothing can fetch them: no connected service
 * exposes billing. Render's MCP server returns services, deploys, logs and
 * metrics; MongoDB's returns collections and queries; Clerk and Anthropic are
 * not connected at all. Stripe is the exception and its fees are already
 * computed from real charges, so it is absent from this form on purpose.
 */
function SettingsPanel({ data, onSaved }) {
  const [open, setOpen] = useState(false);
  const [vals, setVals] = useState(data.settings.amounts || {});
  const [rate, setRate] = useState(data.settings.usd_aud_rate || "");
  const [rateAsOf, setRateAsOf] = useState(data.settings.usd_aud_rate_as_of || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    try {
      setBusy(true);
      await adminSaveCostSettings({
        amounts: vals,
        usd_aud_rate: rate,
        usd_aud_rate_as_of: rateAsOf,
      });
      toast.success("Costs recorded");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#171717]/12 bg-[#F5F2ED] mb-8">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between p-4 text-left"
        data-testid="cost-settings-toggle"
        aria-expanded={open}
      >
        <span className="tick text-[#9a3412]">
          Enter what you were invoiced {data.settings.updated_at ? `· last updated ${data.settings.updated_at.slice(0, 10)}` : "· nothing entered yet"}
        </span>
        <span className="font-mono text-[11px] text-[#525252]">{open ? "close" : "open"}</span>
      </button>

      {open && (
        <div className="px-4 pb-5 border-t border-[#171717]/10 pt-4">
          <p className="text-[11px] font-mono text-[#525252] leading-relaxed mb-4 max-w-3xl">
            {data.settings.billing_not_automatable} Amounts are stored exactly as
            typed and never rounded or converted on save — the USD→AUD rate below
            is applied at display time so you can always see which rate produced
            a figure.
          </p>

          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mb-5">
            {data.settings.editable.map((f) => (
              <label key={f.key} className="block">
                <span className="block text-[11px] font-mono text-[#171717] mb-1">
                  {f.label}{" "}
                  <span className="text-[#525252]">
                    ({f.currency}, {f.cadence})
                  </span>
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  inputMode="decimal"
                  className="input-vault w-full"
                  placeholder="not entered"
                  value={vals[f.key] ?? ""}
                  onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })}
                  data-testid={`cost-input-${f.key}`}
                />
                <span className="block text-[10px] font-mono text-[#525252] mt-1 leading-snug">
                  {f.source}
                </span>
              </label>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 border-t border-[#171717]/10 pt-4">
            <label className="block">
              <span className="block text-[11px] font-mono text-[#171717] mb-1">
                USD → AUD rate <span className="text-[#525252]">(from a card statement)</span>
              </span>
              <input
                type="number" step="0.0001" min="0" inputMode="decimal"
                className="input-vault w-full" placeholder="e.g. 1.5200"
                value={rate} onChange={(e) => setRate(e.target.value)}
                data-testid="cost-input-fx"
              />
              <span className="block text-[10px] font-mono text-[#525252] mt-1 leading-snug">
                Not a live feed on purpose — a mid-market rate is not the rate you were charged.
              </span>
            </label>
            <label className="block">
              <span className="block text-[11px] font-mono text-[#171717] mb-1">
                Rate dated <span className="text-[#525252]">(statement date)</span>
              </span>
              <input
                type="date" className="input-vault w-full"
                value={rateAsOf} onChange={(e) => setRateAsOf(e.target.value)}
                data-testid="cost-input-fx-date"
              />
            </label>
          </div>

          <button
            onClick={save} disabled={busy}
            className="btn-primary mt-5 disabled:opacity-60"
            data-testid="cost-settings-save"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Record these
          </button>
        </div>
      )}
    </div>
  );
}

function Section({ title, kicker, children }) {
  return (
    <section className="mb-10">
      {kicker && <div className="tick text-[#9a3412] mb-1">{kicker}</div>}
      <h3 className="font-display text-[19px] text-[#171717] mb-4">{title}</h3>
      {children}
    </section>
  );
}

export default function CostDashboard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    adminCosts(days)
      .then((d) => { if (live) { setData(d); setErr(null); } })
      .catch(() => { if (live) setErr("Could not load costs."); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [days, reload]);

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 text-[#525252] font-mono text-[12px] py-10">
        <Loader2 size={14} className="animate-spin" /> Loading costs…
      </div>
    );
  }
  if (err) return <div className="font-mono text-[12px] text-[#9a3412] py-10">{err}</div>;
  if (!data) return null;

  const t = data.totals;
  const isFloor = t.is_floor;

  return (
    <div data-testid="cost-dashboard">
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="tick text-[#9a3412] mb-1">Cost of running Margin Lens</div>
          <h2 className="font-display text-[26px] text-[#171717]">
            {isFloor ? "What we can measure so far" : "Monthly run rate"}
          </h2>
        </div>
        <div className="flex gap-1" role="group" aria-label="Reporting period">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              aria-pressed={days === d}
              className={`px-3 py-1.5 text-[11px] font-mono border transition-colors ${
                days === d
                  ? "bg-[#171717] text-[#E8E4DF] border-[#171717]"
                  : "border-[#171717]/20 text-[#525252] hover:border-[#171717]/50"
              }`}
              data-testid={`cost-period-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {isFloor && (
        <div className="border border-[#9a3412]/35 bg-[#9a3412]/[0.06] p-4 mb-8 flex gap-3">
          <AlertTriangle size={15} className="text-[#9a3412] shrink-0 mt-0.5" />
          <div className="text-[12px] font-mono text-[#171717] leading-relaxed">
            <strong>These totals are a floor, not a total.</strong> {t.floor_reason}{" "}
            Costs nobody has entered are listed below as “not entered” rather than
            estimated — real spend is higher than what is shown here.
          </div>
        </div>
      )}

      <SettingsPanel data={data} onSaved={() => setReload((r) => r + 1)} />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
        <Tile
          label="Cash in"
          value={money(data.inflow.gross_aud)}
          sub={`${data.inflow.paid_transactions} paid transaction${data.inflow.paid_transactions === 1 ? "" : "s"}. ${data.inflow.note}`}
        />
        <Tile
          label={`Total cost ${isFloor ? "(floor)" : ""}`}
          value={money(t.total_cost_aud)}
          tone="rust"
          sub={`Fixed ${money(t.monthly_fixed_aud)} + variable ${money(t.variable_aud)}`}
        />
        <Tile
          label="Model spend"
          value={usd(data.variable.llm_cost_usd)}
          sub={`${data.variable.total_calls} recorded call${data.variable.total_calls === 1 ? "" : "s"} over ${data.period_days} days`}
          unavailable={data.variable.llm_cost_aud_unavailable_reason
            ? `No AUD figure: ${data.variable.llm_cost_aud_unavailable_reason}.`
            : null}
        />
        <Tile
          label="Net"
          value={money(t.net_aud)}
          tone="rust"
          sub={Number(t.net_aud) < 0 ? "Pre-revenue — cost with no live sales yet." : "Cash in less measured cost."}
        />
      </div>

      <Section kicker="Unit economics — measured, not apportioned" title="What one sweep and one audit cost">
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          {[["free_sweep", "Free sweep", "Earns nothing — this is acquisition spend"],
            ["paid_audit", "Paid audit", "Against A$990"]].map(([k, label, sub]) => {
            const u = data.unit_economics[k] || {};
            return (
              <div key={k} className="border border-[#171717]/12 bg-[#F5F2ED] p-5">
                <div className="tick text-[#525252] mb-2">{label}</div>
                {u.unavailable_reason ? (
                  <>
                    <div className="font-display text-[20px] text-[#525252] italic">Not yet measurable</div>
                    <div className="mt-2 text-[11px] font-mono text-[#9a3412] leading-snug">
                      {u.unavailable_reason}.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-display text-[28px] leading-none text-[#9a3412]">
                      {u.cost_per_unit_aud ? money(u.cost_per_unit_aud) : usd(u.cost_per_unit_usd)}
                      <span className="text-[13px] text-[#525252]"> / unit</span>
                    </div>
                    <div className="mt-2 text-[11px] font-mono text-[#525252] leading-snug">
                      {u.units} completed · {u.calls} model call{u.calls === 1 ? "" : "s"} ({u.calls_per_unit} each) · {sub}
                      {u.unattributed_calls > 0 && (
                        <span className="text-[#9a3412]">
                          {" "}· {u.unattributed_calls} call{u.unattributed_calls === 1 ? "" : "s"} not tied to a job, excluded
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        <div className="border border-[#171717]/12 bg-[#F5F2ED] p-5">
          <div className="tick text-[#525252] mb-2">Contribution per audit</div>
          <div className="font-display text-[28px] leading-none text-[#171717]">
            {data.unit_economics.contribution_per_audit_aud
              ? money(data.unit_economics.contribution_per_audit_aud)
              : <span className="text-[15px] text-[#525252] italic">not yet measurable</span>}
          </div>
          <div className="mt-2 text-[11px] font-mono text-[#525252] leading-relaxed max-w-2xl">
            {data.unit_economics.contribution_note}
          </div>
          <div className="mt-3 pt-3 border-t border-[#171717]/10 text-[11px] font-mono text-[#525252] leading-relaxed max-w-2xl">
            {data.unit_economics.free_sweep_is_acquisition_cost}
          </div>
          <div className="mt-3 text-[11px] font-mono text-[#525252] leading-relaxed max-w-2xl">
            {data.unit_economics.basis}
          </div>
        </div>
      </Section>

      <Section kicker="Variable — scales with volume" title="Model spend by what it bought">
        <div className="text-[12px] font-mono text-[#525252] mb-3 leading-relaxed">
          {data.variable.recording_started}
        </div>
        {data.variable.by_purpose.length === 0 ? (
          <div className="border border-[#171717]/12 bg-[#F5F2ED] p-5 font-mono text-[12px] text-[#525252]">
            No model calls recorded in this period. Usage recording began 18 Aug 2026 —
            calls made before then were never measured and cannot be recovered.
          </div>
        ) : (
          <div className="overflow-x-auto border border-[#171717]/12">
            <table className="w-full text-[12px] font-mono">
              <thead className="bg-[#171717] text-[#E8E4DF]">
                <tr>
                  <th className="text-left p-3 font-normal">What it bought</th>
                  <th className="text-right p-3 font-normal">Calls</th>
                  <th className="text-right p-3 font-normal">In</th>
                  <th className="text-right p-3 font-normal">Out</th>
                  <th className="text-right p-3 font-normal">Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.variable.by_purpose.map((r) => (
                  <tr key={r.key} className="border-t border-[#171717]/10">
                    <td className="p-3">{r.key.replace(/_/g, " ")}</td>
                    <td className="p-3 text-right">{r.calls}</td>
                    <td className="p-3 text-right">{r.input_tokens.toLocaleString()}</td>
                    <td className="p-3 text-right">{r.output_tokens.toLocaleString()}</td>
                    <td className="p-3 text-right text-[#9a3412]">{usd(r.cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3 text-[11px] font-mono text-[#525252] leading-relaxed">
          {data.rates.thinking_tokens}
        </div>
      </Section>

      <Section kicker="Variable — scales with revenue" title="Payment processing">
        <div className="border border-[#171717]/12 bg-[#F5F2ED] p-5">
          <div className="flex flex-wrap gap-x-10 gap-y-3">
            <div>
              <div className="tick text-[#525252] mb-1">Fees charged</div>
              <div className="font-display text-[22px] text-[#9a3412]">
                {money(data.variable.stripe_fees_aud)}
              </div>
            </div>
            <div>
              <div className="tick text-[#525252] mb-1">Per A$990 audit</div>
              <div className="font-display text-[22px] text-[#171717]">
                {money(data.unit_economics.stripe_fee_per_audit_aud)}
              </div>
            </div>
          </div>
          <div className="mt-4 text-[11px] font-mono text-[#525252] leading-relaxed">
            Domestic {data.rates.stripe.domestic} · international {data.rates.stripe.international}.
            Source: {data.rates.stripe.source}, verified {data.rates.stripe.as_of}. {data.rates.stripe.note}
          </div>
        </div>
      </Section>

      <Section kicker="Fixed — owed whether or not anyone buys" title="Monthly run rate">
        <div className="overflow-x-auto border border-[#171717]/12">
          <table className="w-full text-[12px] font-mono">
            <thead className="bg-[#171717] text-[#E8E4DF]">
              <tr>
                <th className="text-left p-3 font-normal">Line</th>
                <th className="text-left p-3 font-normal">Category</th>
                <th className="text-right p-3 font-normal">Monthly (AUD)</th>
              </tr>
            </thead>
            <tbody>
              {data.fixed.lines.map((l) => (
                <tr key={l.key} className="border-t border-[#171717]/10 align-top">
                  <td className="p-3">
                    <div className="text-[#171717]">{l.label}</div>
                    <div className="text-[11px] text-[#525252] mt-1 leading-snug max-w-xl">{l.why}</div>
                    {l.unavailable_reason && (
                      <div className="text-[11px] text-[#9a3412] mt-1 leading-snug">
                        Enter from: {l.source}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-[#525252]">{l.category}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {l.monthly_aud ? money(l.monthly_aud) : <Unavailable reason={l.unavailable_reason} />}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[#171717]/30 bg-[#F5F2ED]">
                <td className="p-3 text-[#171717]" colSpan={2}>
                  Total {data.fixed.total_is_floor && <span className="text-[#9a3412]">(floor — {data.fixed.unavailable.length} line{data.fixed.unavailable.length === 1 ? "" : "s"} not entered)</span>}
                </td>
                <td className="p-3 text-right text-[#9a3412]">{money(data.fixed.total_aud)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="mt-3 text-[11px] font-mono text-[#525252] leading-relaxed">
          {data.currency_note} FX rate:{" "}
          {data.fixed.fx.usd_aud_rate
            ? `${data.fixed.fx.usd_aud_rate} (${data.fixed.fx.as_of})`
            : "not entered — " + data.fixed.fx.source}
        </div>
      </Section>

      <Section kicker="Method" title="Where every rate came from">
        <div className="border border-[#171717]/12 bg-[#F5F2ED] p-5">
          <table className="w-full text-[11px] font-mono">
            <tbody>
              {data.rates.llm.map((r) => (
                <tr key={r.model} className="align-top">
                  <td className="py-2 pr-4 text-[#171717] whitespace-nowrap">{r.model}</td>
                  <td className="py-2 pr-4 text-[#9a3412] whitespace-nowrap">
                    ${r.input_per_mtok_usd} in / ${r.output_per_mtok_usd} out per 1M
                  </td>
                  <td className="py-2 text-[#525252]">
                    {r.used_for}. {r.source}, {r.as_of}.
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
